import * as _ from 'lodash'
import axios from 'axios'
import * as AsyncLock from 'async-lock'

import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {
  IConnection, IDimension, IDimensionDataProvider, IHierarchy,
  IHierarchyLevel, IHierarchyNode
} from "../PA.Reporting.Core/PA.Reporting.IF";
import {IUsrContext} from "../PA.Core/PA.Core.IF.User";
import {ILease} from "../PA.Utils/PA.UtilsIF";
import {GenericLease} from "../PA.Utils/GenericLease";
import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";


export interface IDimensionDataRequest {
  Dimension: IDimension,
  Connection: IConnection,
  UserId?: string,
  RequestType: string,
  HierarchyId?: string,
  LevelCount?: number

}


export class DimensionDataService
  implements IDimensionDataProvider
{
  static dctResultsByRequestKey: {[id:string]: any[]} = {}
  static dctLock: any = new AsyncLock()
  static lease: ILease

  //make a static lease here -> lock -> check the lease -> reset

  connection: IConnection
  dimension: IDimension

  constructor(pConnection: IConnection, pDimension: IDimension) {
    this.connection = pConnection
    this.dimension = pDimension


  }

  private processRequest(aRequest: IDimensionDataRequest): Promise<any[]> {
    const self = this

    return new Promise<any[]>((resolve, error) => {

      var aUsrCtx = <IUsrContext> PAEnvironment.getValue(PAEnvironment.UserContext)
      aRequest.UserId = aUsrCtx.User.cif.AUTHUSR_ID

      // Create Async Lock Key.
      let aRequestKey = DimensionDataService.getCacheRequestKey(aRequest)

      DimensionDataService.dctLock.acquire(aRequestKey,
        (done: any) => {
          self.checkLease()
              .then(() => {
                // Check if Key is in Cache, if so, return it's results
                if(aRequestKey in DimensionDataService.dctResultsByRequestKey){
                  done(null, DimensionDataService.dctResultsByRequestKey[aRequestKey])
                  return
                }

                // Make the request while holding the lock
                self.makeBackEndRequest(aRequest)
                    .then((res) => {
                      if(_.isArray(res)){
                        DimensionDataService.dctResultsByRequestKey[aRequestKey] = res
                      }
                      done(null, res)
                    }).catch((err) => {
                  done(err)
                })
              })
      }).then((results: any[]) => {
        resolve(results)
      }).catch((err:any) => {
        error(err)
      })


    })

  }

  private static  getCacheRequestKey(aRequest: IDimensionDataRequest): string {
    let BaseKey = aRequest.Connection.ConnectionId + '-' + aRequest.Dimension.DimensionId + '-' + aRequest.RequestType

    switch (aRequest.RequestType) {
      case 'getHierarchies':
        return BaseKey

      case 'getHierarchyLevels':
        return BaseKey + '-' + aRequest.HierarchyId

      case 'getHierarchyNodes':
        return BaseKey + '-' + aRequest.HierarchyId

      default:
        return BaseKey
    }

  }

  private makeBackEndRequest(aRequest: IDimensionDataRequest): Promise<any[]> {
    const self = this

    return new Promise<any[]>((resolve, reject) => {

      var sEndPoint = <string>(PAEnvironment.getValue(PAEnvironment.Network_URL_Base,"/"));
      if(sEndPoint.endsWith('/'))
        sEndPoint += 'dimensionDataService';
      else
        sEndPoint += '/dimensionDataService';

      axios.post(sEndPoint, {data: {request: aRequest}})
        .then((resp: any) => {
          var responseData = resp.data;

          if(_.isArray(responseData)) {
            resolve(responseData);
          } else {
            reject('Unable to parse Dimension Data Response: ' + responseData );
          }
        })
        .catch((err:any) => {
          reject('Unable to obtain Dimension Data from Server. ' + err);
        });

    })
  }

    getHierarchies(): Promise<IHierarchy[]> {
    const self = this

    return new Promise<IHierarchy[]>((resolve, error) => {
        self.processRequest({
          Dimension: self.dimension,
          Connection: self.connection,
          RequestType: 'getHierarchies'
        }).then((results: any[]) => {
          resolve(results)
        }).catch((err) => {
          error(err)
        })
    })

  }

  getHierarchyLevels(HierarchyId: string): Promise<IHierarchyLevel[]> {
    const self = this

    return new Promise<IHierarchyLevel[]>((resolve, error) => {
        self.processRequest({
            Dimension: self.dimension,
            Connection: self.connection,
            RequestType: 'getHierarchyLevels',
            HierarchyId: HierarchyId

        }).then((results: any[]) => {
        resolve(results)
      }).catch((err) => {
        error(err)
      })

    })
  }

  getHierarchyNodes(HierarchyId: string, Level_count?: number): Promise<IHierarchyNode[]> {
    const self = this

    return new Promise<IHierarchyNode[]>((resolve, error) => {
      self.processRequest({
        Dimension: self.dimension,
        Connection: self.connection,
        RequestType: 'getHierarchyNodes',
        HierarchyId: HierarchyId,
        LevelCount:  Level_count || 0

      }).then((results: any[]) => {
        self.registerLease(HierarchyId);
        resolve(results)
      }).catch((err) => {
        error(err)
      })

    })
  }



  private registerLease(hierarchyID: string) {
    DimensionDataService.lease.addClass(hierarchyID)
  }

  private async checkLease() : Promise<void> {
    try {
      if (_.isUndefined(DimensionDataService.lease)) {
        DimensionDataService.lease = new GenericLease(LeaseRegistry.Lease_Type_ReportingDimensionHierarchy);
        DimensionDataService.lease.addClass(LeaseRegistry.Lease_Type_DimensionHierarchy)
      }

      if (await DimensionDataService.lease.hasDataChanged()) {
        this.resetCache();
        DimensionDataService.lease.resetLease();
      }
    }
    catch(err){
      console.log(`Check Lease Error: `)
      console.log(err)
    }
  }

  private resetCache() {
    DimensionDataService.dctResultsByRequestKey = {};
  }

}